import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AdminHeroManager from './components/AdminHeroManager';
import AdminServicesManager from './components/AdminServicesManager';
import AdminGalleryManager from './components/AdminGalleryManager';
import AdminAreasManager from './components/AdminAreasManager';
import AdminContentManager from './components/AdminContentManager';
import AdminFacebookSettings from './components/AdminFacebookSettings';
import AdminMessages from './components/AdminMessages';
import AdminSettings from './components/AdminSettings';
import LanguageSelector from '@/components/base/LanguageSelector';
import AuthGuard from './components/AuthGuard';
import { fetchMessages, fetchQuotes, fetchAppointments, type ContactMessageApi, type QuoteRequestApi, type AppointmentLeadApi } from '@/api/client';
import { showConfirm } from '@/utils/swal';

const getTabs = (t: (k: string) => string) => [
  { id: 'dashboard', label: t('admin_dashboard'), icon: 'ri-dashboard-3-line' },
  { id: 'hero', label: t('admin_hero_slider'), icon: 'ri-image-line' },
  { id: 'services', label: t('admin_services'), icon: 'ri-briefcase-4-line' },
  { id: 'gallery', label: t('admin_gallery'), icon: 'ri-gallery-line' },
  { id: 'areas', label: t('admin_service_areas'), icon: 'ri-map-pin-2-line' },
  { id: 'content', label: t('admin_page_content'), icon: 'ri-article-line' },
  { id: 'facebook', label: t('admin_facebook'), icon: 'ri-facebook-circle-line' },
  { id: 'messages', label: t('admin_messages'), icon: 'ri-mail-unread-line' },
  { id: 'settings', label: t('admin_settings'), icon: 'ri-settings-4-line' },
];

export default function AdminPage() {
  return (
    <AuthGuard>
      <AdminContent />
    </AuthGuard>
  );
}

function AdminContent() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tabs = getTabs(t);
  const activeTabLabel = tabs.find((t2) => t2.id === activeTab)?.label || '';

  const handleLogout = async () => {
    const confirmed = await showConfirm(
      t('admin_logout'),
      t('admin_logout_confirm'),
      t('admin_logout'),
      t('admin_cancel')
    );
    if (!confirmed) return;
    // Always clear local auth immediately and navigate, even if API fails.
    // Never block on the logout endpoint to avoid infinite spinner.
    logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-brand-dark/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo / Brand */}
        <div className="h-[72px] flex items-center gap-3 px-6 py-5 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-brand-mint flex items-center justify-center shrink-0">
            <i className="ri-dashboard-3-line text-white text-xl" />
          </div>
          <div>
            <p className="text-sm font-bold text-brand-dark font-heading leading-tight">{t('admin_website_admin')}</p>
            <p className="text-xs text-brand-muted">{t('admin_complete_home')}</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          <p className="px-3 py-2 text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_manage')}</p>
          {tabs.slice(0, 8).map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer relative ${
                activeTab === tab.id
                  ? 'bg-brand-light text-brand-dark font-semibold'
                  : 'text-brand-muted hover:text-brand-dark hover:bg-gray-50'
              }`}
            >
              {activeTab === tab.id && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-brand-mint rounded-r-full" />
              )}
              <i className={`${tab.icon} text-lg ${activeTab === tab.id ? 'text-brand-mint' : ''}`} />
              {tab.label}
            </button>
          ))}

          <p className="px-3 py-2 mt-4 text-[11px] font-semibold text-brand-muted uppercase tracking-wider">{t('admin_system')}</p>
          <button
            onClick={() => {
              setActiveTab('settings');
              setSidebarOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer relative ${
              activeTab === 'settings'
                ? 'bg-brand-light text-brand-dark font-semibold'
                : 'text-brand-muted hover:text-brand-dark hover:bg-gray-50'
            }`}
          >
            {activeTab === 'settings' && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-brand-mint rounded-r-full" />
            )}
            <i className={`ri-settings-4-line text-lg ${activeTab === 'settings' ? 'text-brand-mint' : ''}`} />
            {t('admin_settings')}
          </button>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 space-y-1">
          <a
            href="/"
            className="flex items-center gap-2.5 text-sm text-brand-muted hover:text-brand-dark transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
          >
            <i className="ri-arrow-left-line text-base" />
            {t('admin_back_website')}
          </a>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors px-3 py-2 rounded-lg cursor-pointer"
          >
            <i className="ri-logout-box-line text-base" />
            {t('admin_logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              aria-label="Toggle sidebar"
            >
              <i className="ri-menu-line text-brand-dark text-lg" />
            </button>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-brand-dark font-heading">{activeTabLabel}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector variant="admin" />
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-brand-dark leading-tight">{user?.name || user?.email || t('admin_administrator')}</p>
                {user?.email && <p className="text-xs text-brand-muted">{user.email}</p>}
              </div>
              <div className="w-9 h-9 rounded-full bg-brand-mint flex items-center justify-center shrink-0">
                <i className="ri-user-3-line text-white" />
              </div>
              <button
                onClick={handleLogout}
                className="hidden md:flex w-9 h-9 items-center justify-center rounded-lg text-brand-muted hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                title={t('admin_logout')}
              >
                <i className="ri-logout-box-r-line text-lg" />
              </button>
            </div>
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
          {activeTab === 'dashboard' && <DashboardTab onNavigate={setActiveTab} />}
          {activeTab === 'hero' && <AdminHeroManager />}
          {activeTab === 'services' && <AdminServicesManager />}
          {activeTab === 'gallery' && <AdminGalleryManager />}
          {activeTab === 'areas' && <AdminAreasManager />}
          {activeTab === 'content' && <AdminContentManager />}
          {activeTab === 'facebook' && <AdminFacebookSettings />}
          {activeTab === 'messages' && <AdminMessages />}
          {activeTab === 'settings' && <AdminSettings />}
        </main>
      </div>
    </div>
  );
}

/* ─── Types ─── */
type ActivityItem =
  | { type: 'message'; data: ContactMessageApi }
  | { type: 'quote'; data: QuoteRequestApi }
  | { type: 'appointment'; data: AppointmentLeadApi }
  | { type: 'ai_lead'; data: AppointmentLeadApi };

interface DashboardTabProps {
  onNavigate: (tabId: string) => void;
}

function DashboardTab({ onNavigate }: DashboardTabProps) {
  const { t } = useTranslation('home');

  const [messages, setMessages] = useState<ContactMessageApi[]>([]);
  const [quotes, setQuotes] = useState<QuoteRequestApi[]>([]);
  const [appointments, setAppointments] = useState<AppointmentLeadApi[]>([]);
  const [aiLeads, setAiLeads] = useState<AppointmentLeadApi[]>([]);

  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [msgsResult, quotesResult, apptsResult] = await Promise.allSettled([
        fetchMessages(),
        fetchQuotes(),
        fetchAppointments(),
      ]);

      if (msgsResult.status === 'fulfilled') {
        setMessages(msgsResult.value);
      } else {
        setMessages([]);
      }

      if (quotesResult.status === 'fulfilled') {
        setQuotes(quotesResult.value);
      } else {
        setQuotes([]);
      }

      if (apptsResult.status === 'fulfilled') {
        setAppointments(apptsResult.value);
        setAiLeads(apptsResult.value.filter((a) => a.source === 'ai_assistant'));
      } else {
        setAppointments([]);
        setAiLeads([]);
      }
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const newMessages = messages.filter((m) => m.status === 'New').length;
  const newQuotes = quotes.filter((q) => q.status === 'new').length;
  const newAppointments = appointments.filter((a) => a.status === 'new' && a.source !== 'ai_assistant').length;
  const newAiLeads = aiLeads.filter((a) => a.status === 'new').length;

  const statCards = [
    {
      label: t('admin_new_messages'),
      value: String(newMessages),
      icon: 'ri-mail-unread-line',
      color: 'bg-brand-mint/10 text-brand-mint',
      tabId: 'messages',
      subtitle: t('admin_pending_replies'),
      accent: newMessages > 0 ? 'border-brand-mint/30' : '',
    },
    {
      label: t('admin_new_quotes'),
      value: String(newQuotes),
      icon: 'ri-file-list-3-line',
      color: 'bg-amber-500/10 text-amber-600',
      tabId: 'messages',
      subtitle: t('admin_pending_quotes'),
      accent: newQuotes > 0 ? 'border-amber-400/30' : '',
    },
    {
      label: t('admin_new_appointments'),
      value: String(newAppointments),
      icon: 'ri-calendar-check-line',
      color: 'bg-blue-500/10 text-blue-600',
      tabId: 'messages',
      subtitle: t('admin_pending_appointments'),
      accent: newAppointments > 0 ? 'border-blue-400/30' : '',
    },
    {
      label: t('admin_new_ai_leads'),
      value: String(newAiLeads),
      icon: 'ri-sparkling-line',
      color: 'bg-teal-500/10 text-teal-600',
      tabId: 'messages',
      subtitle: t('admin_pending_ai_leads'),
      accent: newAiLeads > 0 ? 'border-teal-400/30' : '',
    },
  ];

  /* ─── Recent Activity ─── */
  const activityItems: ActivityItem[] = (() => {
    const all: ActivityItem[] = [
      ...messages.map((d) => ({ type: 'message' as const, data: d })),
      ...quotes.map((d) => ({ type: 'quote' as const, data: d })),
      ...appointments.filter((a) => a.source !== 'ai_assistant').map((d) => ({ type: 'appointment' as const, data: d })),
      ...aiLeads.map((d) => ({ type: 'ai_lead' as const, data: d })),
    ];
    return all
      .sort((a, b) => {
        const dA = new Date(a.data.created_at || 0).getTime();
        const dB = new Date(b.data.created_at || 0).getTime();
        return dB - dA;
      })
      .slice(0, 5);
  })();

  const getActivityTypeLabel = (type: ActivityItem['type']) => {
    switch (type) {
      case 'message': return t('admin_activity_messages');
      case 'quote': return t('admin_activity_quotes');
      case 'appointment': return t('admin_activity_appointments');
      case 'ai_lead': return t('admin_activity_ai_leads');
    }
  };

  const getActivityTypeColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'message': return 'bg-brand-mint text-white';
      case 'quote': return 'bg-amber-500 text-white';
      case 'appointment': return 'bg-blue-500 text-white';
      case 'ai_lead': return 'bg-teal-500 text-white';
    }
  };

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'message': return 'ri-mail-unread-line';
      case 'quote': return 'ri-file-list-3-line';
      case 'appointment': return 'ri-calendar-check-line';
      case 'ai_lead': return 'ri-sparkling-line';
    }
  };

  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const formatDateShort = (value?: string) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return value; }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header + Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-brand-dark font-heading">{t('admin_website_admin')}</h2>
          <p className="text-sm text-brand-muted mt-1">{t('admin_manage_hero')}</p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-brand-dark text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
          {t('admin_dashboard_refresh')}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <div className="w-10 h-10 border-2 border-brand-mint border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-medium text-brand-dark">{t('admin_loading_dashboard')}</p>
        </div>
      )}

      {!loading && loaded && (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statCards.map((stat) => (
              <button
                key={stat.label}
                onClick={() => onNavigate(stat.tabId)}
                className={`bg-white rounded-xl p-5 border border-gray-100 text-left hover:border-brand-mint/30 hover:shadow-md transition-all duration-300 cursor-pointer group ${stat.accent}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-11 h-11 rounded-xl ${stat.color} flex items-center justify-center transition-colors`}>
                    <i className={`${stat.icon} text-xl`} />
                  </div>
                  {Number(stat.value) > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      {stat.value}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-brand-dark font-heading">{stat.value}</p>
                <p className="text-sm font-medium text-brand-dark mt-0.5">{stat.label}</p>
                <p className="text-xs text-brand-muted mt-1">{stat.subtitle}</p>
              </button>
            ))}
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quick Actions */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-base font-bold text-brand-dark font-heading mb-4">{t('admin_edit_hero')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    label: t('admin_edit_hero'),
                    desc: t('admin_update_slider'),
                    icon: 'ri-image-line',
                    tabId: 'hero',
                    color: 'bg-brand-mint/10 text-brand-mint hover:bg-brand-mint hover:text-white',
                  },
                  {
                    label: t('admin_add_service'),
                    desc: t('admin_create_service'),
                    icon: 'ri-briefcase-4-line',
                    tabId: 'services',
                    color: 'bg-brand-mint/10 text-brand-mint hover:bg-brand-mint hover:text-white',
                  },
                  {
                    label: t('admin_upload_image'),
                    desc: t('admin_add_gallery'),
                    icon: 'ri-gallery-line',
                    tabId: 'gallery',
                    color: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500 hover:text-white',
                  },
                  {
                    label: t('admin_view_messages'),
                    desc: t('admin_check_quotes'),
                    icon: 'ri-mail-unread-line',
                    tabId: 'messages',
                    color: 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white',
                  },
                ].map((action) => (
                  <button
                    key={action.label}
                    onClick={() => onNavigate(action.tabId)}
                    className={`flex items-center gap-4 p-4 rounded-xl border border-gray-100 text-left transition-all duration-200 cursor-pointer hover:shadow-sm ${action.color}`}
                  >
                    <i className={`${action.icon} text-2xl`} />
                    <div>
                      <p className="text-sm font-semibold">{action.label}</p>
                      <p className="text-xs opacity-70 mt-0.5">{action.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-brand-dark font-heading">{t('admin_recent_activity')}</h3>
                <button
                  onClick={() => onNavigate('messages')}
                  className="text-xs text-brand-mint font-medium hover:underline cursor-pointer"
                >
                  {t('admin_view_all')}
                </button>
              </div>

              {activityItems.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-inbox-line text-gray-300 text-2xl" />
                  </div>
                  <p className="text-sm font-medium text-brand-dark">{t('admin_activity_empty')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityItems.map((item, idx) => (
                    <div
                      key={`${item.type}-${item.data.id}-${idx}`}
                      onClick={() => onNavigate('messages')}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className={`w-9 h-9 rounded-full ${getActivityTypeColor(item.type)} flex items-center justify-center shrink-0`}>
                        <i className={`${getActivityIcon(item.type)} text-sm`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-brand-dark truncate">
                            {item.data.full_name}
                          </p>
                          {item.type === 'ai_lead' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 bg-teal-500 text-white text-[9px] font-bold rounded-full uppercase">
                              AI
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-brand-muted truncate">
                          {getActivityTypeLabel(item.type)}
                          {item.type === 'message' && (item.data as ContactMessageApi).service_type ? ` · ${(item.data as ContactMessageApi).service_type}` : ''}
                          {item.type === 'quote' && (item.data as QuoteRequestApi).property_type ? ` · ${(item.data as QuoteRequestApi).property_type}` : ''}
                          {item.type === 'appointment' && (item.data as AppointmentLeadApi).appointment_type ? ` · ${(item.data as AppointmentLeadApi).appointment_type}` : ''}
                          {item.type === 'ai_lead' && (item.data as AppointmentLeadApi).appointment_type ? ` · ${(item.data as AppointmentLeadApi).appointment_type}` : ''}
                        </p>
                        <p className="text-xs text-brand-muted mt-0.5">{formatDateShort(item.data.created_at)}</p>
                      </div>
                      <span
                        className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          item.data.status === 'New' || item.data.status === 'new'
                            ? 'bg-amber-100 text-amber-700'
                            : item.data.status === 'Contacted' || item.data.status === 'contacted'
                              ? 'bg-blue-100 text-blue-700'
                              : item.data.status === 'Resolved' || item.data.status === 'completed' || item.data.status === 'won'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {item.data.status === 'New' ? t('admin_status_new')
                          : item.data.status === 'new' ? t('admin_status_new')
                          : item.data.status === 'Contacted' ? t('admin_status_contacted')
                          : item.data.status === 'contacted' ? t('admin_status_contacted')
                          : item.data.status === 'Resolved' ? t('admin_status_resolved')
                          : item.data.status === 'completed' ? t('admin_status_completed')
                          : item.data.status === 'won' ? t('admin_status_won')
                          : item.data.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Stats Summary Bar */}
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-brand-dark font-heading">{messages.length}</p>
                <p className="text-xs text-brand-muted mt-1">{t('admin_tab_contact')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-dark font-heading">{quotes.length}</p>
                <p className="text-xs text-brand-muted mt-1">{t('admin_tab_quotes')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-dark font-heading">{appointments.filter((a) => a.source !== 'ai_assistant').length}</p>
                <p className="text-xs text-brand-muted mt-1">{t('admin_tab_appointments')}</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-dark font-heading">{aiLeads.length}</p>
                <p className="text-xs text-brand-muted mt-1">{t('admin_tab_ai_leads')}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}